// ─── Types ─────────────────────────────────────────────────────────────────────

export type PriorityStatus = "en_attente" | "valide" | "rejete";
export type PriorityRole = "CITIZEN" | "ONG";

// Tranches d'âge adoptées sur toute la plateforme
export const AGE_GROUPS = [
  { id: "18-24", label: "18 – 24 ans" },
  { id: "25-29", label: "25 – 29 ans" },
  { id: "30-44", label: "30 – 44 ans" },
  { id: "45-59", label: "45 – 59 ans" },
  { id: "60+",   label: "60 ans et +" },
] as const;

export type AgeGroupId = typeof AGE_GROUPS[number]["id"];

/** Calcule automatiquement la tranche depuis une date de naissance ISO ("YYYY-MM-DD") */
export function computeAgeGroup(birthDate: string): AgeGroupId | undefined {
  if (!birthDate) return undefined;
  const today = new Date();
  const birth = new Date(birthDate);
  if (isNaN(birth.getTime())) return undefined;
  const age =
    today.getFullYear() -
    birth.getFullYear() -
    (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0);
  if (age < 18) return undefined; // non autorisé
  if (age <= 24) return "18-24";
  if (age <= 29) return "25-29";
  if (age <= 44) return "30-44";
  if (age <= 59) return "45-59";
  return "60+";
}

/** Vrai si Gen Z (-29 ans inclus : tranches 18-24 et 25-29) */
export function isGenZ(ageGroup?: string): boolean {
  return ageGroup === "18-24" || ageGroup === "25-29";
}

export interface CitizenPriority {
  id: string;
  userId: string;
  userName: string;
  role: PriorityRole;
  genre?: string;       // "homme" | "femme"
  ageGroup?: AgeGroupId;
  ongName?: string;
  priority1: string;    // max 3 mots
  priority2: string;    // max 3 mots
  status: PriorityStatus;
  submittedAt: string;
  validatedAt?: string;
}

// ─── Validation du texte de priorité ──────────────────────────────────────────

export function validatePriorityText(text: string): { valid: boolean; error?: string } {
  const trimmed = text.trim();
  if (!trimmed) return { valid: false, error: "Champ obligatoire" };
  const words = trimmed.split(/\s+/);
  if (words.length > 3) return { valid: false, error: "Maximum 3 mots" };
  if (trimmed.length > 40) return { valid: false, error: "Trop long" };
  return { valid: true };
}

import { getApiUrl } from "@/utils/apiConfig";
import { create } from 'zustand';

const API_URL = getApiUrl();

// ─── Seed Data ─────────────────────────────────────────────────────────────────

const SEED_PRIORITIES: CitizenPriority[] = [
  // Citoyens hommes
  { id: "p01", userId: "u1",  userName: "Ahmed Benali",          role: "CITIZEN", genre: "homme", ageGroup: "25-29", priority1: "Emploi jeunes",          priority2: "Transport public",          status: "valide",    submittedAt: "2026-03-01", validatedAt: "2026-03-02" },
  { id: "p02", userId: "u2",  userName: "Omar Tazi",             role: "CITIZEN", genre: "homme", ageGroup: "30-44", priority1: "Santé accessible",        priority2: "Eau potable",              status: "valide",    submittedAt: "2026-03-02", validatedAt: "2026-03-03" },
  { id: "p03", userId: "u3",  userName: "Hassan Idrissi",        role: "CITIZEN", genre: "homme", ageGroup: "18-24", priority1: "Emploi jeunes",          priority2: "Logement abordable",       status: "valide",    submittedAt: "2026-03-03", validatedAt: "2026-03-04" },
  { id: "p04", userId: "u4",  userName: "Rachid Meknassi",       role: "CITIZEN", genre: "homme", ageGroup: "25-29", priority1: "Corruption zéro",        priority2: "Emploi jeunes",            status: "valide",    submittedAt: "2026-03-04", validatedAt: "2026-03-05" },
  { id: "p05", userId: "u5",  userName: "Youssef Chakir",        role: "CITIZEN", genre: "homme", ageGroup: "45-59", priority1: "Retraite digne",         priority2: "Santé accessible",         status: "valide",    submittedAt: "2026-03-05", validatedAt: "2026-03-06" },
  // Citoyennes femmes
  { id: "p06", userId: "u6",  userName: "Fatima Alami",          role: "CITIZEN", genre: "femme", ageGroup: "25-29", priority1: "Égalité femmes",         priority2: "Emploi jeunes",            status: "valide",    submittedAt: "2026-03-06", validatedAt: "2026-03-07" },
  { id: "p07", userId: "u7",  userName: "Khadija Ouazzani",      role: "CITIZEN", genre: "femme", ageGroup: "30-44", priority1: "Violences faites femmes", priority2: "Santé accessible",        status: "valide",    submittedAt: "2026-03-07", validatedAt: "2026-03-08" },
  { id: "p08", userId: "u8",  userName: "Aicha Bensouda",        role: "CITIZEN", genre: "femme", ageGroup: "18-24", priority1: "Égalité femmes",         priority2: "Éducation qualité",        status: "valide",    submittedAt: "2026-03-08", validatedAt: "2026-03-09" },
  { id: "p09", userId: "u9",  userName: "Zineb Fassi",           role: "CITIZEN", genre: "femme", ageGroup: "25-29", priority1: "Garde enfants",          priority2: "Emploi jeunes",            status: "valide",    submittedAt: "2026-03-09", validatedAt: "2026-03-10" },
  { id: "p10", userId: "u10", userName: "Malika Tihami",         role: "CITIZEN", genre: "femme", ageGroup: "45-59", priority1: "Retraite digne",         priority2: "Santé accessible",         status: "valide",    submittedAt: "2026-03-10", validatedAt: "2026-03-11" },
  // Gen Z
  { id: "p11", userId: "u11", userName: "Karim El Fassi",        role: "CITIZEN", genre: "homme", ageGroup: "18-24", priority1: "Emploi jeunes",          priority2: "Sport culture",            status: "valide",    submittedAt: "2026-03-11", validatedAt: "2026-03-12" },
  { id: "p12", userId: "u12", userName: "Mehdi Berrada",         role: "CITIZEN", genre: "homme", ageGroup: "25-29", priority1: "Startup entrepreneuriat", priority2: "Transport public",        status: "valide",    submittedAt: "2026-03-12", validatedAt: "2026-03-13" },
  { id: "p13", userId: "u13", userName: "Amine Lahlou",          role: "CITIZEN", genre: "homme", ageGroup: "18-24", priority1: "Numérique emploi",       priority2: "Logement abordable",       status: "valide",    submittedAt: "2026-03-13", validatedAt: "2026-03-14" },
  // ONG
  { id: "p14", userId: "u14", userName: "Association Jil Jadid", role: "ONG", ongName: "Jil Jadid",      priority1: "Emploi jeunes",          priority2: "Corruption zéro",          status: "valide",    submittedAt: "2026-03-14", validatedAt: "2026-03-15" },
  { id: "p15", userId: "u15", userName: "Forum Femmes Maroc",    role: "ONG", ongName: "Forum Femmes",   priority1: "Égalité femmes",         priority2: "Violences faites femmes",  status: "valide",    submittedAt: "2026-03-15", validatedAt: "2026-03-16" },
  { id: "p16", userId: "u16", userName: "Réseau Eau Vie",        role: "ONG", ongName: "Réseau Eau",     priority1: "Eau potable",            priority2: "Environnement propre",     status: "valide",    submittedAt: "2026-03-16", validatedAt: "2026-03-17" },
  { id: "p17", userId: "u17", userName: "Assoc. Santé Rural",    role: "ONG", ongName: "Santé Rural",    priority1: "Santé accessible",       priority2: "Désenclavement rural",     status: "valide",    submittedAt: "2026-03-17", validatedAt: "2026-03-18" },
  // En attente de validation
  { id: "p18", userId: "u18", userName: "Samir Kettani",         role: "CITIZEN", genre: "homme", ageGroup: "30-44", priority1: "Justice sociale",        priority2: "Éducation qualité",        status: "en_attente", submittedAt: "2026-04-01" },
  { id: "p19", userId: "u19", userName: "Nadia Amrani",          role: "CITIZEN", genre: "femme", ageGroup: "25-29", priority1: "Égalité salariale",      priority2: "Transport public",         status: "en_attente", submittedAt: "2026-04-02" },
  { id: "p20", userId: "u20", userName: "Tarik Maarouf",         role: "CITIZEN", genre: "homme", ageGroup: "18-24", priority1: "Housing project",        priority2: "Sport culture",            status: "en_attente", submittedAt: "2026-04-03" },
];

// ─── CRUD ──────────────────────────────────────────────────────────────────────

// ─── Zustand Store ─────────────────────────────────────────────────────────────

interface PrioritiesStore {
  priorities: CitizenPriority[];
  isLoaded: boolean;
  fetchPriorities: () => Promise<void>;
  submitPriority: (data: Omit<CitizenPriority, "id" | "status" | "submittedAt" | "validatedAt">) => Promise<void>;
  adminValidatePriority: (id: string, status: "valide" | "rejete") => Promise<void>;
  adminEditPriority: (id: string, priority1: string, priority2: string) => Promise<void>;
  adminDeletePriority: (id: string) => Promise<void>;
}

export const usePrioritiesStore = create<PrioritiesStore>((set, get) => ({
  priorities: [],
  isLoaded: false,

  fetchPriorities: async () => {
    try {
      const res = await fetch(`${API_URL}/priorities.php`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          set({ priorities: data, isLoaded: true });
        }
      }
    } catch (err) {
      console.error("Error fetching priorities:", err);
    }
  },

  submitPriority: async (data) => {
    const newEntry: CitizenPriority = {
      id: `p${Date.now()}`,
      ...data,
      priority2: data.priority2 || "",
      status: "en_attente",
      submittedAt: new Date().toISOString(),
    };

    // Optimistic update
    set((state) => ({ priorities: [newEntry, ...state.priorities] }));

    try {
      await fetch(`${API_URL}/priorities.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'submit', ...newEntry })
      });
    } catch (err) {
      console.error("Error submitting priority:", err);
    }
  },

  adminValidatePriority: async (id, status) => {
    const validatedAt = status === "valide" ? new Date().toISOString().slice(0, 10) : undefined;
    
    // Optimistic update
    set((state) => ({
      priorities: state.priorities.map(p => p.id === id ? { ...p, status, validatedAt } : p)
    }));

    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await fetch(`${API_URL}/priorities.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'validate', id, status })
      });
    } catch (err) {
      console.error("Error validating priority:", err);
    }
  },

  adminEditPriority: async (id, priority1, priority2) => {
    // Optimistic update
    set((state) => ({
      priorities: state.priorities.map(p => p.id === id ? { ...p, priority1: priority1.trim(), priority2: priority2.trim() } : p)
    }));

    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await fetch(`${API_URL}/priorities.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'edit', id, priority1, priority2 })
      });
    } catch (err) {
      console.error("Error editing priority:", err);
    }
  },

  adminDeletePriority: async (id) => {
    // Optimistic update
    set((state) => ({
      priorities: state.priorities.filter(p => p.id !== id)
    }));

    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await fetch(`${API_URL}/priorities.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'delete', id })
      });
    } catch (err) {
      console.error("Error deleting priority:", err);
    }
  }
}));

// Load immediately if running in browser
if (typeof window !== 'undefined') {
  usePrioritiesStore.getState().fetchPriorities();
}

// Keep helper functions for backward compatibility where needed, but it's better to use the store hook directly
export function getValidatedPriorities(): CitizenPriority[] {
  return usePrioritiesStore.getState().priorities.filter((p) => p.status === "valide");
}
export function getAllPriorities(): CitizenPriority[] {
  return usePrioritiesStore.getState().priorities;
}

// ─── Analytics des priorités ───────────────────────────────────────────────────

export interface PriorityFrequency {
  word: string;
  count: number;
}

export const KEYWORD_TRANSLATIONS: Record<string, Record<string, string>> = {
  "emploi jeunes": { ar: "تشغيل الشباب", zgh: "ⵜⴰⵡⵓⵔⵉ ⵉ ⵉⵄⵔⵔⵉⵎⵏ" },
  "transport public": { ar: "النقل العمومي", zgh: "ⴰⵙⵙⴰⵢ ⴰⴳⴷⵓⴷⴰⵏ" },
  "santé accessible": { ar: "صحة في المتناول", zgh: "ⵜⴰⴷⵓⵙⵉ ⵉ ⴽⵓⵢⴰⵏ" },
  "eau potable": { ar: "الماء الصالح", zgh: "ⴰⵎⴰⵏ ⵏ ⵜⵉⵙⵙⵉ" },
  "logement abordable": { ar: "سكن لائق", zgh: "ⵜⴰⵣⴷⴷⵓⵖⵜ ⵉⵖⵓⴷⴰⵏ" },
  "corruption zéro": { ar: "محاربة الفساد", zgh: "ⴰⵎⵏⵖⵉ ⵎⴳⴰⵍ ⴰⵙⵕⴽⴽⵓ" },
  "retraite digne": { ar: "تقاعد كريم", zgh: "ⴰⵙⵓⵔⴼ ⵉⵖⵓⴷⴰⵏ" },
  "égalité femmes": { ar: "المساواة", zgh: "ⵜⴰⴳⴰⴷⴰ ⵏ ⵜⵎⵖⴰⵔⵉⵏ" },
  "violences faites femmes": { ar: "محاربة العنف", zgh: "ⴰⵎⵏⵖⵉ ⵎⴳⴰⵍ ⵜⴰⴽⵕⵉⴹⵜ" },
  "éducation qualité": { ar: "تعليم جيد", zgh: "ⴰⵙⴳⵎⵉ ⵉⵖⵓⴷⴰⵏ" },
  "garde enfants": { ar: "رعاية الأطفال", zgh: "ⴰⵃⵟⵟⵓ ⵏ ⵉⵎⵥⵢⴰⵏⵏ" },
  "sport culture": { ar: "الرياضة والثقافة", zgh: "ⵜⵓⵏⵏⵓⵏⵜ ⴷ ⵜⴷⵍⵙⴰ" },
  "startup entrepreneuriat": { ar: "ريادة الأعمال", zgh: "ⵜⵉⵡⵉⵙⵉ ⵉ ⵜⵎⵙⵙⵏⵜⵉⵢⵉⵏ" },
  "numérique emploi": { ar: "الاقتصاد الرقمي", zgh: "ⵜⴰⴷⴰⵎⵙⴰ ⵜⴰⵎⵓⵟⵟⵓⵏⵜ" },
  "environnement propre": { ar: "بيئة نظيفة", zgh: "ⵜⴰⵡⵏⵏⴰⴹⵜ ⵜⴰⵣⴷⴷⴰⴳⵜ" },
  "désenclavement rural": { ar: "فك العزلة", zgh: "ⴰⵙⵉⴼⴹ ⵏ ⵜⵓⵏⵏⵓⵖⵜ" },
  "justice sociale": { ar: "العدالة الاجتماعية", zgh: "ⵜⴰⵣⵔⴼⵜ ⵜⴰⵏⴰⵎⵓⵏⵜ" },
  "égalité salariale": { ar: "المساواة الأجرية", zgh: "ⵜⴰⴳⴰⴷⴰ ⴳ ⵡⴰⴳⴰⵔ" },
  "housing project": { ar: "مشاريع سكنية", zgh: "ⵉⵙⵏⴼⴰⵔⵏ ⵏ ⵜⵣⴷⴷⵓⵖⵜ" }
};

function normalizePriorityKey(text: string): string {
  const t = text.trim().toLowerCase();
  for (const [frKey, langs] of Object.entries(KEYWORD_TRANSLATIONS)) {
    if (t === frKey || t === langs.ar || t === langs.zgh) return frKey;
  }
  return t;
}

export function computePriorityStats(filter?: {
  role?: "CITIZEN" | "ONG";
  genre?: "homme" | "femme";
  genZOnly?: boolean;   // Gen Z (-29 ans : tranches 18-24 et 25-29)
  plus29Only?: boolean; // +29 ans citoyens (tranches 30-44, 45-59, 60+)
}): PriorityFrequency[] {
  let data = getValidatedPriorities();

  if (filter?.role)      data = data.filter((p) => p.role === filter.role);
  if (filter?.genre)     data = data.filter((p) => p.genre === filter.genre);
  if (filter?.genZOnly)  data = data.filter((p) => isGenZ(p.ageGroup));
  if (filter?.plus29Only) data = data.filter((p) => !isGenZ(p.ageGroup) && p.role === "CITIZEN");

  const freq: Record<string, number> = {};
  data.forEach((p) => {
    [p.priority1, p.priority2].forEach((text) => {
      if (!text) return;
      const key = normalizePriorityKey(text);
      freq[key] = (freq[key] || 0) + 1;
    });
  });

  return Object.entries(freq)
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count);
}
